import { memo, useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ArrowRight, Home } from 'lucide-react';
import { useEventsQuery } from '@/hooks/useEventsQuery';
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from '@/components/ui/carousel';
import { CardSkeleton } from '@/components/common/PageSkeleton';
import { getOptimizedImageUrl } from '@/utils/imageOptimization';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type AutoplayType from 'embla-carousel-autoplay';

import { useScrollReveal } from '@/hooks/useScrollReveal';
import RevealBlock from '@/components/common/RevealBlock';

const UpcomingEvents = memo(() => {
  const { events, isLoading } = useEventsQuery();
  const { ref: sectionRef, visible: sectionVisible } = useScrollReveal(0.1);

  const upcomingEvents = useMemo(() => {
    return events.filter(event => new Date(event.data_evento) >= new Date());
  }, [events]);

  const [autoplayPlugin, setAutoplayPlugin] = useState<ReturnType<typeof AutoplayType> | null>(null);

  useEffect(() => {
    if (sectionVisible && !autoplayPlugin) {
      import('embla-carousel-autoplay').then((mod) => {
        setAutoplayPlugin(mod.default({ delay: 3000, stopOnInteraction: false, playOnInit: true }));
      });
    }
  }, [sectionVisible, autoplayPlugin]);

  const isSingleEvent = upcomingEvents.length === 1;

  if (isLoading) {
    return (
      <section ref={sectionRef} className="py-20 md:py-28 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="h-12 w-64 mx-auto bg-white/5 animate-pulse rounded mb-4" />
            <div className="h-6 w-96 mx-auto bg-white/5 animate-pulse rounded" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <CardSkeleton key={i} />)}
          </div>
        </div>
      </section>
    );
  }

  if (!upcomingEvents || upcomingEvents.length === 0) {
    return (
      <section ref={sectionRef} className="py-20 md:py-28 bg-black">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight">
            <span className="text-white">Próximos </span><span className="sobre-gradient-text">Eventos</span>
          </h2>
          <p className="text-white/50 mb-6">Nenhum evento próximo agendado</p>
          <Link to="/cadastre-evento">
            <button className="sobre-cta-button text-white px-8 py-4 rounded-xl font-medium inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
              <Calendar size={20} />
              Cadastre seu evento gratuitamente
            </button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="py-20 md:py-28 sobre-mission-bg relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="sobre-orb sobre-orb-2" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <RevealBlock>
          <div className="text-center mb-4">
            <p className="text-sm tracking-[0.25em] uppercase text-white/40 mb-4 font-medium">Agenda</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
              <span className="text-white">Próximos </span><span className="sobre-gradient-text">Eventos</span>
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">Não perca os próximos shows e eventos</p>
          </div>
        </RevealBlock>

        <RevealBlock delay={200}>
          <div className="mt-8">
            <Carousel
              opts={{ align: isSingleEvent ? 'center' : 'start', loop: true }}
              plugins={autoplayPlugin ? [autoplayPlugin] : []}
              className="w-full max-w-7xl mx-auto"
            >
              <CarouselContent className={`-ml-2 md:-ml-4 ${isSingleEvent ? 'justify-center' : ''}`}>
                {upcomingEvents.map((event, idx) => (
                  <CarouselItem key={event.id} className="pl-2 md:pl-4 basis-[85%] sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <Link to={`/evento/${event.id}`} className="block h-full">
                      <div className="sobre-glass-card rounded-xl h-full cursor-pointer group overflow-hidden">
                        <div className="flex flex-col h-full">
                          <div className="relative aspect-video overflow-hidden rounded-t-xl">
                            {event.imagem_url ? (
                              <img
                                src={getOptimizedImageUrl(event.imagem_url, { width: 400, height: 225 })}
                                alt={event.nome_evento}
                                className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110 blur-[2px] group-hover:blur-0"
                                loading="lazy" decoding="async" width={400} height={225}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[hsl(221,89%,15%)] to-[hsl(280,59%,15%)] flex items-center justify-center">
                                <Calendar size={64} className="text-white/15" />
                              </div>
                            )}
                            {/* Date badge */}
                            <div className="absolute top-3 right-3 sobre-cta-button text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                              {format(new Date(event.data_evento), "dd MMM", { locale: ptBR })}
                            </div>
                          </div>

                          <div className="p-5 flex flex-col flex-grow text-center">
                            <h3 className="text-lg font-bold text-white mb-3 line-clamp-2 group-hover:sobre-gradient-text transition-all duration-300">
                              {event.nome_evento}
                            </h3>
                            <div className="space-y-1.5">
                              {event.local_evento && (
                                <div className="flex items-center justify-center gap-1.5 text-white/40 text-sm">
                                  <MapPin size={13} className="flex-shrink-0" />
                                  <p className="line-clamp-1">{event.local_evento}</p>
                                </div>
                              )}
                              {event.endereco && (
                                <div className="flex items-center justify-center gap-1.5 text-white/30 text-xs">
                                  <Home size={11} className="flex-shrink-0" />
                                  <p className="line-clamp-1">{event.endereco}</p>
                                </div>
                              )}
                              {(event.cidade || event.estado) && (
                                <div className="flex items-center justify-center gap-1.5 text-white/30 text-xs">
                                  <MapPin size={11} className="flex-shrink-0" />
                                  <p>{event.cidade}{event.estado ? ` - ${event.estado}` : ''}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex border-white/10 bg-black/40 hover:bg-black/60 hover:border-white/20 text-white" />
              <CarouselNext className="hidden md:flex border-white/10 bg-black/40 hover:bg-black/60 hover:border-white/20 text-white" />
            </Carousel>
          </div>
        </RevealBlock>

        <RevealBlock delay={350}>
          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/agenda-eventos">
              <button className="sobre-cta-button text-white group px-8 py-4 rounded-xl font-medium text-base inline-flex items-center gap-3 hover:opacity-90 transition-all duration-300 hover:scale-[1.02]">
                Ver toda a agenda
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/cadastre-evento">
              <button className="px-8 py-4 rounded-xl font-medium text-base inline-flex items-center gap-3 border border-white/10 text-white/70 hover:bg-white/5 hover:border-white/20 transition-all duration-300">
                <Calendar size={18} />
                Cadastre seu evento
              </button>
            </Link>
          </div>
        </RevealBlock>
      </div>
    </section>
  );
});

UpcomingEvents.displayName = 'UpcomingEvents';
export default UpcomingEvents;